import { decreaseItemQuantity } from "@/actions/selection/client/decrease-item-quantity"
import { deleteItemFromBasket } from "@/actions/selection/client/delete-item-from-basket"
import { increaseItemQuantity } from "@/actions/selection/client/increase-item-quantity"
import { type ProductNoticesCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/productNoticesCopy"
import BasketProductCardSkeleton from "@/app/(site)/[locale]/checkout/CheckoutContent/components/Loaders/BasketProductCardSkeleton"
import { type QuantityChangeAction } from "@/app/(site)/[locale]/checkout/types"
import BasketProductCard from "@/app/(site)/_components/Organisms/Cards/ProductCards/BasketProductCard"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { usePfasValidation } from "@/app/(site)/_utils/_hooks/usePfasValidation"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { useCheckoutFormStore } from "@/app/(site)/contexts/Global"
import logger from "@/lib/logger/logger-init"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { isFinalSaleCustomer, isFinalSaleVariant } from "@/src/utils/checkout/finalSale"
import { useAppForCurrentLocale } from "@/src/utils/locale/useAppForCurrentLocale"
import { t } from "@/translations"
import { useState } from "react"

export default function OrderSummaryProductDetails({
    products,
    langCode,
    voucherLoading,
    isLoadingSelection,
    oneProduct,
    hideBasketFunctionality,
    productNoticesCopy
}: {
    products: any[]
    langCode: string
    voucherLoading: boolean
    isLoadingSelection: boolean
    oneProduct?: boolean
    hideBasketFunctionality?: boolean
    productNoticesCopy?: ProductNoticesCopy
}) {
    const { selection, mutate, location } = useSWRBasketSessionState()
    const [quantityLoading, setQuantityLoading] = useState<string | null>(null)
    const { checkoutCurrentStep } = useEcommerceStore()
    const siteContext = useSiteContext()
    const { countryCode } = useAppForCurrentLocale()

    /**
     * What the customer is typing, which is ahead of the committed selection. Nullish
     * fallbacks on purpose: a field they have emptied must read as empty rather than fall
     * back to the address Centra still holds.
     */
    const typedZipCode = useCheckoutFormStore(state => state.shippingAddress.zipCode)
    const typedState = useCheckoutFormStore(state => state.shippingAddress.state)

    const { restrictedProducts } = usePfasValidation({
        country: selection?.shippingAddress?.country,
        state: typedState ?? selection?.shippingAddress?.state,
        zipCode: typedZipCode ?? selection?.shippingAddress?.zipCode
    })

    const isUsCustomer = isFinalSaleCustomer(
        countryCode,
        location?.country ?? selection?.shippingAddress?.country
    )
    const finalSaleMessage =
        productNoticesCopy?.finalSaleMessage || t(langCode, "checkout.final_sale.message")

    const isProductPfasRestricted = (productName: string) => {
        return restrictedProducts.some(
            restricted =>
                restricted?.productName?.toLowerCase() === productName?.toLowerCase()
        )
    }

    const handleQuantityChange = async (lineId: string, action: QuantityChangeAction) => {
        setQuantityLoading(lineId)
        try {
            let result

            const formData = new FormData()
            switch (action) {
                case "increase":
                    formData.append("item", lineId)
                    result = await increaseItemQuantity(formData)
                    const increaseItem = selection?.items.find(
                        (item: any) => item.item === lineId
                    )
                    const increaseProduct = increaseItem?.product
                    if (increaseProduct) {
                        await import("@/lib/utils/tracking/gtmEvents").then(
                            ({ addToCartEvent }) => {
                                addToCartEvent(
                                    increaseProduct,
                                    increaseItem,
                                    siteContext,
                                    "cart"
                                )
                            }
                        )
                    }
                    break
                case "decrease":
                    formData.append("item", lineId)
                    result = await decreaseItemQuantity(formData)
                    const decreaseItem = selection?.items.find(
                        (item: any) => item.line === lineId
                    )
                    const decreaseProduct = decreaseItem?.product
                    if (decreaseProduct) {
                        import("@/lib/utils/tracking/gtmEvents").then(
                            ({ removeFromCartEvent }) => {
                                removeFromCartEvent(
                                    decreaseProduct,
                                    decreaseItem,
                                    1,
                                    siteContext,
                                    "cart"
                                )
                            }
                        )
                    }
                    break
                case "delete":
                    result = await deleteItemFromBasket(lineId)
                    const deleteItem = selection?.items.find(
                        (item: any) => item.line === lineId
                    )
                    const deleteProduct = deleteItem?.product
                    if (deleteProduct) {
                        import("@/lib/utils/tracking/gtmEvents").then(
                            ({ removeFromCartEvent }) => {
                                removeFromCartEvent(
                                    deleteProduct,
                                    deleteItem,
                                    deleteItem?.quantity ?? 1,
                                    siteContext,
                                    "cart"
                                )
                            }
                        )
                    }
                    break
            }

            if (result && result.success) {
                await mutate()
            } else {
                throw new Error(result?.error?.message || "Failed to update quantity")
            }
        } catch (error) {
            logger.error("Error updating quantity:", error)
        } finally {
            setQuantityLoading(null)
        }
    }

    return (
        <>
            {isLoadingSelection ? (
                <>
                    <BasketProductCardSkeleton />
                </>
            ) : (
                products.map((product: any, index: number) => {
                    const hasDiscount =
                        (Array.isArray(product.discounts?.vouchers) &&
                            product.discounts.vouchers.length > 0) ||
                        (Array.isArray(product.discounts?.automaticDiscounts) &&
                            product.discounts.automaticDiscounts.length > 0)
                    const isLast = index === products.length - 1

                    return (
                        <BasketProductCard
                            key={product.line}
                            product={product.product}
                            lineId={product.line}
                            itemId={product.item}
                            quantity={product.quantity}
                            oneProduct={oneProduct}
                            containerClassName="px-12 sm:px-16 md:px-24"
                            totalPriceBeforeDiscount={product.totalPriceBeforeDiscount}
                            totalPriceAfterDiscount={product.totalPriceAfterDiscount}
                            hasDiscount={hasDiscount}
                            size={product.size}
                            langCode={langCode}
                            checkoutCurrentStep={checkoutCurrentStep}
                            pfasRestricted={
                                !hideBasketFunctionality
                                    ? isProductPfasRestricted(product.product?.name || "")
                                    : false
                            }
                            isFinalSale={
                                isUsCustomer && isFinalSaleVariant(product.product)
                            }
                            finalSaleMessage={finalSaleMessage}
                            isQuantityLoading={
                                !hideBasketFunctionality
                                    ? quantityLoading === product.line ||
                                      quantityLoading === product.item
                                    : false
                            }
                            isVoucherLoading={voucherLoading}
                            handleQuantityChange={
                                !hideBasketFunctionality
                                    ? (lineId: string, action: QuantityChangeAction) => {
                                          handleQuantityChange(lineId, action)
                                      }
                                    : () => {}
                            }
                            hideBasketFunctionality={hideBasketFunctionality}
                            isLast={isLast}
                        />
                    )
                })
            )}
        </>
    )
}
